// Edit/delete form for a single BoardColumn. Soft-delete is the only
// destructive action — runs already tagged keep their `boardColumnId`
// (the column is tombstoned, not erased), so the triage board's
// historical bucketing is preserved.
// (DEV-16183.)
//
// Frontend validation (DEV-16193):
//   - Name: required, trimmed, deduplicated against OTHER columns in
//     the org so the user can keep their own name unchanged.
//   - Description capped at 400 chars to match the server schema.
import { useClient } from '@cortex/react-query';
import type { BoardColumn, BoardColumnColor, IPaginated } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';

import { BackButton } from '../../components/common/BackButton';
import { EmptyState } from '../../components/common/EmptyState';
import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { routes } from '../../lib/constants';

import {
  descriptionMaxLength,
  isBoardColumnFormValid,
  nameMaxLength,
  validateBoardColumnForm,
} from './-validation';

export const Route = createFileRoute('/board-columns/$columnId')({
  component: BoardColumnDetailPage,
});

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };
const fieldErrorStyle: CSSProperties = {
  color: 'var(--status-danger)',
  fontSize: 'var(--fs-xs)',
};
const counterStyle: CSSProperties = {
  fontSize: 11,
  color: 'var(--text-muted)',
  textAlign: 'right',
};

const colorOptions: Array<{ value: BoardColumnColor | ''; label: string }> = [
  { value: '', label: 'None (neutral)' },
  { value: 'teal', label: 'Teal' },
  { value: 'iris', label: 'Iris' },
  { value: 'amber', label: 'Amber' },
  { value: 'rose', label: 'Rose' },
  { value: 'mint', label: 'Mint' },
  { value: 'lavender', label: 'Lavender' },
];

function BoardColumnDetailPage() {
  const { columnId } = Route.useParams();
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();

  const detail = useQuery({
    queryKey: ['board-columns', columnId],
    queryFn: () => client.http.get(`v1/board-columns/${columnId}`).json<BoardColumn>(),
  });
  // List driving the duplicate-name check. Exclude the row being
  // edited from the set so the user can keep its original name. Same
  // cache key the admin list page uses, so this is usually warm.
  const columnsQuery = useQuery({
    queryKey: ['board-columns'],
    queryFn: () => client.http.get('v1/board-columns?limit=500').json<IPaginated<BoardColumn>>(),
  });
  const otherNamesLower = useMemo(() => {
    const s = new Set<string>();
    for (const c of columnsQuery.data?.items ?? []) {
      if (c.id !== columnId) s.add(c.name.toLowerCase());
    }
    return s;
  }, [columnsQuery.data, columnId]);

  const [name, setName] = useState('');
  const [color, setColor] = useState<BoardColumnColor | ''>('');
  const [description, setDescription] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Submit-then-validate (Formik's `submitCount > 0` pattern). Stays
  // quiet until first Save attempt; revalidates live after that so the
  // user sees errors clear as they fix them.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Hydrate the form once the row loads. We re-hydrate whenever the
  // server version changes too, so an external rename via the API
  // doesn't leave the form in a stale-overwrite state.
  useEffect(() => {
    const row = detail.data;
    if (!row) return;
    setName(row.name);
    setColor((row.color as BoardColumnColor | null) ?? '');
    setDescription(row.description ?? '');
  }, [detail.data]);

  const errors = useMemo(
    () =>
      validateBoardColumnForm({
        values: { name, color, description },
        existingNamesLower: otherNamesLower,
      }),
    [name, color, description, otherNamesLower],
  );
  const isValid = isBoardColumnFormValid(errors);

  const update = useMutation({
    mutationFn: () =>
      client.http
        .patch(`v1/board-columns/${columnId}`, {
          json: {
            name: name.trim(),
            color: color === '' ? null : color,
            description: description.trim() === '' ? null : description.trim(),
          },
        })
        .json<BoardColumn>(),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['board-columns'] }),
        qc.invalidateQueries({ queryKey: ['board-columns', columnId] }),
      ]);
    },
  });

  const remove = useMutation({
    mutationFn: () => client.http.delete(`v1/board-columns/${columnId}`).text(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['board-columns'] });
      try {
        await nav({ to: routes.BOARD_COLUMNS });
      } catch (err) {
        console.error(err);
      }
    },
  });

  if (detail.isLoading) return <Spinner />;
  if (detail.error)
    return (
      <div className="alert alert--danger">
        Failed to load board column: {(detail.error as Error).message}
      </div>
    );
  if (!detail.data)
    return <EmptyState title="Not found" description="Board column does not exist." />;

  const showNameError = submitAttempted && errors.name;
  const showDescriptionError = submitAttempted && errors.description;

  return (
    <>
      <PageHeader
        title={detail.data.name}
        actions={<BackButton to={routes.BOARD_COLUMNS} label="Board columns" />}
      />
      <form
        style={stack3}
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitAttempted(true);
          if (!isValid) return;
          update.mutate();
        }}
        noValidate={true}
      >
        <label className="form-row">
          <span className="t-mono-label">NAME</span>
          <input
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={nameMaxLength}
            aria-invalid={Boolean(showNameError)}
            aria-describedby={showNameError ? 'board-column-edit-name-error' : undefined}
          />
          {showNameError ? (
            <span id="board-column-edit-name-error" style={fieldErrorStyle} role="alert">
              {errors.name}
            </span>
          ) : null}
        </label>
        <label className="form-row">
          <span className="t-mono-label">COLOR</span>
          <select
            className="input"
            value={color}
            onChange={(e) => setColor(e.target.value as BoardColumnColor | '')}
          >
            {colorOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="form-row">
          <span className="t-mono-label">DESCRIPTION (optional)</span>
          <textarea
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            maxLength={descriptionMaxLength}
            aria-invalid={Boolean(showDescriptionError)}
          />
          <div style={counterStyle}>
            {description.length} / {descriptionMaxLength}
          </div>
          {showDescriptionError ? (
            <span style={fieldErrorStyle} role="alert">
              {errors.description}
            </span>
          ) : null}
        </label>
        {update.error ? (
          <div className="alert alert--danger">{(update.error as Error).message}</div>
        ) : null}
        <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'space-between' }}>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={update.isPending || (submitAttempted && !isValid)}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
          {confirmDelete ? (
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setConfirmDelete(false)}
                disabled={remove.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--destructive btn--sm"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
              >
                {remove.isPending ? 'Deleting…' : 'Confirm delete'}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setConfirmDelete(true)}
            >
              Delete column
            </button>
          )}
        </div>
        {remove.error ? (
          <div className="alert alert--danger">{(remove.error as Error).message}</div>
        ) : null}
      </form>
    </>
  );
}
